
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import { Header, Footer } from "./PDFcomponents";

import openSans from '../../../../fonts/OpenSans-Regular.ttf';
import openSansBold from '../../../../fonts/OpenSans-Bold.ttf';
import openSansItalic from '../../../../fonts/OpenSans-Italic.ttf';
import { CustomerState } from '../../../../redux/customer';
import { ChildCustomerState } from '../../../../redux/childCustomer';

Font.register({ family: 'Open Sans', fonts: [{ src: openSans }, { src: openSansBold, fontWeight: 'bold' }, { src: openSansItalic, fontStyle: 'italic' }] });

const COMPLIANCE_BLUE = '#0084b7';
const COMPLIANCE_CRANBERRY = '#9f000f';
const COLORED_ROW = '#e9ecef';

const PAGE_PADDING_BOTTOM = 88;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Open Sans',
    fontSize: '10pt',
    paddingBottom: PAGE_PADDING_BOTTOM
  },
  checklistSectionHeading: {
    backgroundColor: COMPLIANCE_BLUE,
    color: 'white',
    fontWeight: 'bold',
    fontSize: '20pt',
    padding: '5pt'
  },
  content: {
    marginHorizontal: '0.25in',
  },
  errorMessage: {
    color: COMPLIANCE_CRANBERRY,
    fontStyle: 'italic'
  },
  table: {
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: '10pt'
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  tableCol: {
    flex: 1,
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },
  usersCol: {
    flex: 2
  },
  tableCell: {
    margin: 5
  },
  tableHeader: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: '10pt'
  },
  usersText: {
    fontSize: '8pt'
  },
  coloredTableRow: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: COLORED_ROW
  },  
});

const handleRowColor = (index: number) => {
  return index % 2 === 0
    ? styles.tableRow
    : styles.coloredTableRow
};

const formatUsersForPdf = (users: string) =>
  users
    .split(', ')
    .map((user) => user.trim())
    .filter(Boolean)
    .join('\n');

const sortByLocation = (a: { location: string }, b: { location: string }) =>
  a.location.toUpperCase().localeCompare(b.location.toUpperCase());

type EPHIColumnKeys = 'id' | 'location' | 'serialNumber' | 'deviceType' | 'hasPhi' | 'users';

interface EphiTeamMemberPDFProps {
  electronicMediaInventory: Record<EPHIColumnKeys, string>[],
  customer: CustomerState | ChildCustomerState;
  title: string;
  year?: string;
}

const ElectronicMediaInventoryPDF = ({ electronicMediaInventory, customer, title, year }: EphiTeamMemberPDFProps) => {
  if (!title || !year || !customer.customer_number) {
    return;
  }

  const sortedInventory = [...electronicMediaInventory].sort(sortByLocation);

  return (
    <Document>
      <Page size="A4" orientation="landscape" wrap style={styles.page}>
        <Header customer={customer} title={title} year={year} />
        <View style={styles.content}>
          <View style={styles.checklistSectionHeading}>
            <Text>Electronic Media Inventory</Text>
          </View>
          {sortedInventory.length > 0 ? (
            <View style={styles.table}>
              <View style={styles.tableRow} wrap={false}>
                <View style={[styles.tableCol, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Location of Device</Text>
                </View>
                <View style={[styles.tableCol, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Serial Number of Device</Text>
                </View>
                <View style={[styles.tableCol, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Manufacturer/Type of Device</Text>
                </View>
                <View style={[styles.tableCol, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Stores ePHI</Text>
                </View>
                <View style={[styles.tableCol, styles.usersCol, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Assigned User(s)</Text>
                </View>
              </View>
              {sortedInventory.map(({ id, location, serialNumber, deviceType, hasPhi, users }, i) => (
                <View key={id} wrap={false} style={handleRowColor(i)}>
                  <View style={styles.tableCol}>
                    <View style={styles.tableCell}>
                      <Text>{location}</Text>
                    </View>
                  </View>
                  <View style={styles.tableCol}>
                    <View style={styles.tableCell}>
                      <Text>{serialNumber}</Text>
                    </View>
                  </View>
                  <View style={styles.tableCol}>
                    <View style={styles.tableCell}>
                      <Text>{deviceType}</Text>
                    </View>
                  </View>
                  <View style={styles.tableCol}>
                    <View style={styles.tableCell}>
                      <Text>{hasPhi}</Text>
                    </View>
                  </View>
                  <View style={[styles.tableCol, styles.usersCol]}>
                    <View style={styles.tableCell}>
                      <Text style={styles.usersText}>{formatUsersForPdf(users)}</Text>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.errorMessage}>No data is available for this section</Text>
          )}
        </View>
        <Footer />
      </Page>
    </Document>
  );
};

export default ElectronicMediaInventoryPDF;
